import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  HomeIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  CogIcon,
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  TicketIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../services/users';
import { NavigationItem, filterNavigationByRole } from '../../utils/permissions';
import { useTranslation } from 'react-i18next';

const navigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  {
    name: 'Events',
    href: '/events',
    icon: ExclamationTriangleIcon,
    requiredRoles: [UserRole.ANALYST, UserRole.ADMINISTRATOR],
  },
  { name: 'Assets', href: '/assets', icon: ServerIcon },

  {
    name: 'VIPs e Fornecedores',
    href: '/registration',
    icon: CogIcon,
    requiredRoles: [UserRole.CLIENT]
  },

  {
    name: 'Companies',
    href: '/companies',
    icon: BuildingOfficeIcon,
    requiredRoles: [UserRole.ANALYST, UserRole.ADMINISTRATOR],
  },
  {
    name: 'Users',
    href: '/users',
    icon: UsersIcon,
    requiredRoles: [UserRole.ADMINISTRATOR],
  },
  {
    name: 'Collectors',
    href: '/collectors',
    icon: CpuChipIcon,
    requiredRoles: [UserRole.ANALYST, UserRole.ADMINISTRATOR],
  },
  { name: 'Reports', href: '/reports', icon: DocumentTextIcon },
  { name: 'Tickets', href: '/tickets', icon: TicketIcon },
  {
    name: 'Domain Investigation',
    href: '/domain-investigation',
    icon: GlobeAltIcon,
    requiredRoles: [UserRole.ADMINISTRATOR],
  },
  { name: 'Leaked Credentials', href: '/leaked-credentials', icon: ShieldCheckIcon },
  {
    name: 'APT Feed',
    href: '/apt-feed',
    icon: SignalIcon,
  },
];

const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Se não há usuário logado, não mostra nada
  if (!user) {
    return null;
  }

  // Filtra a navegação baseada na role do usuário
  const filteredNavigation = filterNavigationByRole(navigation, user.role);

  return (
    <div className='hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col'>
      {/* Sidebar background with glass effect */}
      <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-white/70 backdrop-blur-xl border-r border-white/40 px-6 pb-4 dark:bg-gray-900/80 dark:border-white/10 shadow-glass-lg transition-all duration-300'>
        <div className='flex h-20 shrink-0 items-center mt-2'>
          <div className='flex items-center space-x-3 px-2'>
            <div className='h-10 w-10 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20'>
              <span className='text-white font-bold text-xl'>M</span>
            </div>
            <span className='text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300'>
              Mermaid
            </span>
          </div>
        </div>
        <nav className='flex flex-1 flex-col'>
          <ul className='flex flex-1 flex-col gap-y-7'>
            <li>
              <ul className='-mx-3 space-y-2'>
                {filteredNavigation.map(item => (
                  <li key={item.name}>
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        `group flex gap-x-3 rounded-xl p-3 text-sm leading-6 font-medium transition-all duration-200 ${isActive
                          ? 'bg-white/80 shadow-md text-primary-600 dark:bg-white/10 dark:text-white dark:shadow-none translate-x-1'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-white/50 hover:translate-x-1 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5'
                        }`
                      }
                    >
                      <item.icon
                        className={({ isActive }: { isActive: boolean }) =>
                          `h-6 w-6 shrink-0 transition-colors duration-200 ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'
                          }`
                        }
                        aria-hidden='true'
                      />
                      {t(item.name)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
